import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SmsLogDTO,
    useDeleteSmsLogApi,
    useSaveSmsLogApi,
    useSearchSmsLogApi,
} from '../../../api/sms-log-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { DateCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSmsLogModal } from './save-sms-log-modal';

export function SmsLogApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSmsLog, setSelectedSmsLog] = useState<SmsLogDTO | undefined>();
    const smsLogInit = useSearchSmsLogApi();
    const saveSmsLog = useSaveSmsLogApi();
    const deleteSmsLog = useDeleteSmsLogApi();

    const { result, error, busy, initialized, initializing } = smsLogInit;

    const smsStatusTitle = useCallback(
        (id?: string) => {
            var smsStatus = result?.smsStatuses.find(x => x.id === id);
            return <span>{smsStatus ? `${smsStatus.title} ` : id}</span>;
        },
        [result?.smsStatuses]
    );

    const smsTypeTitle = useCallback(
        (id?: string) => {
            var smsType = result?.smsTypes.find(x => x.id === id);
            return <span>{smsType ? `${smsType.title} ` : id}</span>;
        },
        [result?.smsTypes]
    );

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<SmsLogDTO>[] = useMemo(
        () => [
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 80,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>type-id</T>,
                name: 'typeId',
                width: 150,
                Cell: ({ row }) => smsTypeTitle(row.typeId),
            },
            {
                Header: <T>status-id</T>,
                name: 'statusId',
                width: 80,
                Cell: ({ row }) => smsStatusTitle(row.statusId),
            },
            {
                Header: <T>phone-number</T>,
                name: 'phoneNumber',
                width: 80,
            },
            {
                Header: <T>user-id</T>,
                name: 'userName',
                width: 80,
                Cell: ({ row }) => userTitle(row.userName),
            },
            {
                Header: <T>sent-at</T>,
                name: 'sentAt',
                width: 80,
                Cell: DateCell,
            },
        ],
        [smsStatusTitle, smsTypeTitle, userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        smsLogInit.post(null, {});
    }, [initialized, initializing, error, smsLogInit]);

    const handleDelete = (smsLog: SmsLogDTO) => {
        bd.msgbox(
            translate('delete-sms-log'),
            translate('confirm-delete-sms-log'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSmsLog.remove(
                            smsLog,
                            () => {
                                smsLogInit.updateResult({
                                    ...result,
                                    smsLogs: result.smsLogs.filter(b => b.serial !== smsLog.serial),
                                });
                                notify.dark(translate('selected-sms-log-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedSmsLog(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (smsLog: SmsLogDTO) => {
        saveSmsLog.post(
            null,
            smsLog,
            res => {
                if (selectedSmsLog) {
                    smsLogInit.updateResult({
                        ...result,
                        smsLogs: result.smsLogs.map(b =>
                            b.serial === smsLog.serial ? res.smsLog : b
                        ),
                    });
                } else {
                    smsLogInit.updateResult({
                        ...result,
                        smsLogs: [res.smsLog, ...result.smsLogs],
                    });
                }

                setShowModal(false);
                setSelectedSmsLog(undefined);
                notify.success(
                    selectedSmsLog
                        ? translate('selected-sms-log-updated-successfully')
                        : translate('new-sms-log-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => smsLogInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar onGoBack="/home" title="sms-reports" />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.smsLogs}
                        onRowClick={({ row }) => {
                            setSelectedSmsLog(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveSmsLogModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSmsLog(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedSmsLog={selectedSmsLog}
                        smsStatuses={result?.smsStatuses}
                        smsTypes={result?.smsTypes}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
